import { Box } from "@chakra-ui/react";
import {
  InitialConfigType,
  LexicalComposer,
} from "@lexical/react/LexicalComposer";
import { EditorState, LexicalEditor, SerializedEditorState } from "lexical";
import { useMemo } from "react";
import { useSettings } from "./context/SettingsContext";
import { SharedHistoryContext } from "./context/SharedHistoryContext";
import { ToolbarContext } from "./context/ToolbarContext";
import { FullVersionEditor } from "./editors/FullVersionEditor";
import { LiteVersionEditor } from "./editors/LiteVersionEditor";
import { useHTMLConfig } from "./hooks/useHTMLConfig";
import AllNodes from "./nodes/AllNodes";
import LexicalEditorTheme from "./themes/LexicalEditorTheme";

export type EditorVersion = "full" | "lite";
export type EditorRef = (editor: LexicalEditor) => void;
export type MaybeStringifiedEditorState = string | SerializedEditorState;

export type EditorProps = {
  editorState?: MaybeStringifiedEditorState;
  name?: string;
  id?: string;
  isReadOnly?: boolean;
  placeholder?: string;
  maxLength?: number;
  width?: string;
  height?: string;
  editorRef?: EditorRef;
  onChange?: (editorState: EditorState) => void;
  onBlur?: (editorState: EditorState) => void;
  editorBorderColor?: string;
  editorVersion?: EditorVersion;
};

const Editor = ({
  editorState,
  isReadOnly,
  editorBorderColor,
  editorVersion = "full",
  ...editorProps
}: EditorProps) => {
  const { settings } = useSettings();
  const htmlConfig = useHTMLConfig(settings);
  const initialConfig: InitialConfigType = useMemo(
    () => ({
      editorState: editorState
        ? typeof editorState === "string"
          ? editorState
          : JSON.stringify(editorState)
        : undefined,
      nodes: [...AllNodes],
      onError: console.error,
      namespace: "LexicalEditor",
      theme: LexicalEditorTheme,
      editable: !isReadOnly,
      html: htmlConfig,
    }),
    [editorState, isReadOnly, htmlConfig]
  );

  return (
    <LexicalComposer initialConfig={initialConfig}>
      <SharedHistoryContext>
        <ToolbarContext>
          <Box
            border="1px solid"
            borderColor={editorBorderColor || "gray.200"}
            borderRadius="md"
            _focusWithin={{
              borderColor: "blue.500",
              boxShadow: "0 0 0 1px #3182ce",
            }}
          >
            {editorVersion === "full" ? (
              <FullVersionEditor {...editorProps} />
            ) : (
              <LiteVersionEditor {...editorProps} />
            )}
          </Box>
        </ToolbarContext>
      </SharedHistoryContext>
    </LexicalComposer>
  );
};

export default Editor;
